import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post, del } from '../lib/api.js';

interface EmailSecurityDomain {
  id: string;
  domain: string;
  is_monitored: boolean;
  spf_status?: string;
  dkim_status?: string;
  dmarc_status?: string;
  dmarc_policy?: string;
  overall_score: number;
  last_checked_at?: string;
}

interface DomainsResponse {
  data: EmailSecurityDomain[];
  hasMore?: boolean;
}

export function EmailDomainsPage() {
  const { data: domainsData, loading, error, refresh } = useApi<DomainsResponse>('/email-security/domains');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formDomain, setFormDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const domains = domainsData?.data ?? [];

  const fullyProtected = domains.filter(d =>
    d.spf_status === 'pass' && d.dkim_status === 'pass' && (d.dmarc_status === 'pass')
  ).length;
  const actionRequired = domains.filter(d =>
    d.spf_status === 'missing' || d.dkim_status === 'missing' || d.dmarc_status === 'missing'
  ).length;

  function statusBadge(status?: string) {
    if (!status) return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unchecked</span>;
    const colors =
      status === 'pass' ? 'bg-emerald-100 text-emerald-700' :
      status === 'fail' ? 'bg-red-100 text-red-700' :
      status === 'missing' ? 'bg-red-100 text-red-700' :
      'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{status}</span>;
  }

  async function handleAddDomain(e: React.FormEvent) {
    e.preventDefault();
    if (!formDomain.trim()) return;
    setSubmitting(true);
    try {
      await post('/email-security/domains', { domain: formDomain.trim() });
      setShowAddForm(false);
      setFormDomain('');
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add domain');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCheck(domainId: string) {
    setActionLoading(domainId);
    try {
      await post(`/email-security/domains/${domainId}?action=check`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to check domain');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(domainId: string) {
    if (!confirm('Remove this domain from monitoring?')) return;
    setActionLoading(domainId);
    try {
      await del(`/email-security/domains/${domainId}`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove domain');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCheckAll() {
    setActionLoading('check-all');
    try {
      await post('/email-security/domains?action=check_all');
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to check all domains');
    } finally {
      setActionLoading(null);
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading email domains...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Domains</h2>
      <p className="text-gray-600 mb-6">Monitor SPF, DKIM, and DMARC configuration for your domains.</p>

      <div className="flex items-center justify-between mb-4">
        <div className="grid grid-cols-3 gap-4 flex-1 mr-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Total Domains</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{domains.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Fully Protected</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{fullyProtected}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Action Required</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{actionRequired}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCheckAll}
            disabled={actionLoading === 'check-all'}
            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 disabled:opacity-50 whitespace-nowrap"
          >
            {actionLoading === 'check-all' ? 'Checking...' : 'Check All'}
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 whitespace-nowrap"
          >
            + Add Domain
          </button>
        </div>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddDomain} className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <input
                type="text"
                value={formDomain}
                onChange={(e) => setFormDomain(e.target.value)}
                placeholder="example.com"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                required
              />
            </div>
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50">
              {submitting ? 'Adding...' : 'Add'}
            </button>
            <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Domain</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">SPF</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">DKIM</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">DMARC</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Score</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Last Checked</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {domains.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No domains configured. Add one to start monitoring.</td></tr>
            ) : domains.map((d) => (
              <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{d.domain}</td>
                <td className="px-4 py-3">{statusBadge(d.spf_status)}</td>
                <td className="px-4 py-3">{statusBadge(d.dkim_status)}</td>
                <td className="px-4 py-3">{statusBadge(d.dmarc_status)}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`font-bold ${d.overall_score >= 80 ? 'text-emerald-600' : d.overall_score >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {d.overall_score}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{d.last_checked_at ? new Date(d.last_checked_at).toLocaleDateString() : 'Never'}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleCheck(d.id)}
                    disabled={actionLoading === d.id}
                    className="text-xs text-emerald-600 hover:text-emerald-800 mr-3 disabled:opacity-50"
                  >
                    Check
                  </button>
                  <button
                    onClick={() => handleDelete(d.id)}
                    disabled={actionLoading === d.id}
                    className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
